import { parseGameKey, refKey } from 'config-hub-server/src/model/keys.js';
import type { AdoptRequest } from './adopt.js';
import { resourceFilterFor, writeAuditFilter } from './auditFilter.js';
import { countOf, isEntityType, LABELS, type EntityType } from './labels.js';
import { countWord, type TypeLabel } from './pluralise.js';
import { rowFilterQuery } from './rowFilter.js';
import {
    AUDIT_ROUTE,
    configEditRoute,
    CONFIGS_ROUTE,
    gameAssetsRoute,
    gameEditRoute,
    GAMES_ROUTE,
    gamesRoute,
    newGameRoute,
    NEW_CONFIG_PATH,
    NEW_STUDIO_PATH,
    splitGameKey,
    STUDIOS_ROUTE,
    studioEditRoute,
    withQuery,
} from './routes.js';
import type { SyncStatus } from './status.js';

export type { EntityType };

/**
 * Everything the client knows per entity type, in one place. Adding a type is adding one entry
 * here (and its tRPC calls in `hooks/entityCalls.ts`), not a search for `=== 'game'`.
 */
export type EntityTypeDef = {
    label: TypeLabel;
    /** The create page; a child type takes its parent's key. */
    newRoute: (parentKey?: string | null) => string;
    editRoute: (key: string) => string;
    /** Where the record's row lives in the grids. */
    gridRoute: (key: string) => string;
    /** The grid page itself, unfiltered — where a status link into this type lands. */
    listRoute: string;
    /** The parent's type, for a child type; null for a root. */
    parentType: EntityType | null;
    /** The parent's key, for a child type; null for a root. */
    parentKeyOf: (key: string) => string | null;
    /** The adopt confirm's noun phrase for `n` records of this type — a studio's names the games that come with it. */
    adoptNoun: (n: number) => string;
    /** The wire's own name for this type's key on an adopt / **Adopt to master** input (`{ id }` vs `{ key }` vs `{ name }`) — what tells the types apart with no type tag alongside. */
    wireKeyField: 'id' | 'key' | 'name';
    /** The wire's own name for this type's keys on a bulk adopt input (`{ ids }` vs `{ keys }` vs `{ names }`) — read the same way as `wireKeyField`. */
    wireKeysField: 'ids' | 'keys' | 'names';
    /** How many master children a grid row of this type reports; null when the type has none. */
    childCountOf: (row: { childCount?: number } | null | undefined) => number | null;
    /** The confirm modal's scope line for a master-only delete. */
    deleteScope: (childCount: number | null) => string;
    /** The editor's Assets section, for types that have one; absent for types that don't. */
    assetsRoute?: (key: string) => string;
};

export const ENTITY_TYPES: Record<EntityType, EntityTypeDef> = {
    studio: {
        label: LABELS.studio,
        newRoute: () => NEW_STUDIO_PATH,
        editRoute: studioEditRoute,
        gridRoute: () => STUDIOS_ROUTE,
        listRoute: STUDIOS_ROUTE,
        parentType: null,
        parentKeyOf: () => null,
        adoptNoun: (n) => `${countOf('studio', n)} and the games under ${n === 1 ? 'it' : 'them'}`,
        wireKeyField: 'id',
        wireKeysField: 'ids',
        childCountOf: (row) => row?.childCount ?? null,
        deleteScope: (childCount) =>
            `This studio${childCount ? ` and its ${countWord(childCount, 'master game', 'master games')}` : ''} will be deleted from master.`,
    },
    game: {
        label: LABELS.game,
        newRoute: (parentKey) => newGameRoute(parentKey),
        editRoute: (key) => {
            const { studio, id } = splitGameKey(key);
            return gameEditRoute(studio, id);
        },
        gridRoute: (key) => gamesRoute(splitGameKey(key).studio),
        listRoute: GAMES_ROUTE,
        parentType: 'studio',
        parentKeyOf: (key) => parseGameKey(key)?.studio ?? null,
        adoptNoun: (n) => countOf('game', n),
        wireKeyField: 'key',
        wireKeysField: 'keys',
        childCountOf: () => null,
        deleteScope: () => 'This game will be deleted from master.',
        assetsRoute: (key) => {
            const { studio, id } = splitGameKey(key);
            return gameAssetsRoute(studio, id);
        },
    },
    config: {
        label: LABELS.config,
        newRoute: () => NEW_CONFIG_PATH,
        editRoute: configEditRoute,
        gridRoute: () => CONFIGS_ROUTE,
        listRoute: CONFIGS_ROUTE,
        parentType: null,
        parentKeyOf: () => null,
        adoptNoun: (n) => countOf('config', n),
        wireKeyField: 'name',
        wireKeysField: 'names',
        childCountOf: () => null,
        deleteScope: () => 'This config will be deleted from master.',
    },
};

export { isEntityType };

/** Every type, parents before children — the registry's own order. */
export const ENTITY_TYPE_ORDER = Object.keys(ENTITY_TYPES) as EntityType[];

/** The type whose parent is `type` — a studio's games — or null when `type` has none. */
export const childTypeOf = (type: EntityType): EntityType | null =>
    ENTITY_TYPE_ORDER.find((candidate) => ENTITY_TYPES[candidate].parentType === type) ?? null;

/** Named records of one type, adopted from one environment in one request. */
export const adoptRecordsFor = (
    type: EntityType,
    environment: string,
    keys: string[],
): Extract<AdoptRequest, { scope: 'records' }> => ({
    environment,
    scope: 'records',
    type,
    keys,
    count: keys.length,
});

/** One record, adopted from one environment. */
export const adoptOneFor = (type: EntityType, environment: string, key: string): AdoptRequest =>
    adoptRecordsFor(type, environment, [key]);

/** Some per-environment fields of one record, saved as overrides for that environment. */
export const adoptFieldsFor = (type: EntityType, environment: string, key: string, fields: string[]): AdoptRequest => ({
    ...adoptRecordsFor(type, environment, [key]),
    fields,
});

/** The grid for `type`, filtered to the rows whose own status in `environment` is `status`. */
export const gridStatusRoute = (type: EntityType, environment: string, status: SyncStatus): string =>
    withQuery(ENTITY_TYPES[type].listRoute, rowFilterQuery(environment, status));

export const editRouteFor = (type: EntityType, key: string): string => ENTITY_TYPES[type].editRoute(key);
export const gridRouteFor = (type: EntityType, key: string): string => ENTITY_TYPES[type].gridRoute(key);
export const newRouteFor = (type: EntityType, key: string): string =>
    ENTITY_TYPES[type].newRoute(ENTITY_TYPES[type].parentKeyOf(key));
/** The editor's Assets section, or null for a type that doesn't have one. */
export const assetsRouteFor = (type: EntityType, key: string): string | null =>
    ENTITY_TYPES[type].assetsRoute?.(key) ?? null;

/** `/audit` filtered to one master record. */
export const auditRouteFor = (type: EntityType, key: string): string => {
    const query = writeAuditFilter(
        new URLSearchParams(),
        resourceFilterFor('master', refKey({ type, key })),
    ).toString();
    return withQuery(AUDIT_ROUTE, query);
};

/** The inverse: `studio/<id>` or `game/<studio>/<id>` → a record, or null for anything else (a joined bulk id, a stranger). */
export const parseMasterResourceId = (resourceId: string): { type: EntityType; key: string } | null => {
    const slash = resourceId.indexOf('/');
    if (slash < 0) return null;
    const type = resourceId.slice(0, slash);
    const key = resourceId.slice(slash + 1);
    return isEntityType(type) && key && !key.includes(',') ? { type, key } : null;
};

/**
 * Which type an adopt / **Adopt to master** wire input names, from its own key field (`id` vs
 * `key`) rather than a type tag alongside it — `wireKeyField` read in registry order, first
 * match wins. Used where a payload crosses the wire untyped, e.g. the audit log's sentence for
 * `master/adopt` (`pages/audit/auditSentence.ts`), which has only the stored `input` to read.
 */
export const wireEntityTypeOf = (input: Record<string, unknown>): EntityType | null =>
    ENTITY_TYPE_ORDER.find((type) => ENTITY_TYPES[type].wireKeyField in input) ?? null;
